import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { EnvoiPrerequisDialog } from '../components/prerequis/EnvoiPrerequisDialog';
import {
  ArrowLeft, Save, Loader2, AlertTriangle, CheckCircle2, ShieldAlert,
  MapPin, Ruler, ArrowUpDown, Building2,
  ChevronDown, ChevronUp, Zap, Droplets, Truck, Camera, MessageSquare,
  Upload, Trash2, X, Image as ImageIcon, Send, Ban, RotateCcw, ShieldCheck, Clock, WifiOff, History, Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  brouillon: { label: "Brouillon", color: "bg-slate-100 text-slate-700" },
  incomplet: { label: "Incomplet", color: "bg-amber-100 text-amber-800" },
  a_completer: { label: "À compléter", color: "bg-orange-100 text-orange-800" },
  soumis: { label: "Soumis", color: "bg-blue-100 text-blue-800" },
  valide: { label: "Validé", color: "bg-green-100 text-green-800" },
  valide_reserves: { label: "Validé avec réserves", color: "bg-yellow-100 text-yellow-800" },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-800" },
  a_corriger: { label: "À corriger", color: "bg-orange-100 text-orange-800" },
};

const ENSEIGNE_OPTIONS = ["Leclerc", "Intermarché", "Système U", "Carrefour", "Autre"];
const ZONE_OPTIONS = ["Zone Entrée", "Zone Fleg", "Zone Allée Promo", "Zone poissonnerie", "Entre R Trad.", "Zone du fond", "Autre"];
const OBSTACLES_PLAFOND = [
  { key: "luminaire", label: "Luminaire" }, { key: "gaine", label: "Gaine" },
  { key: "sprinkler", label: "Sprinkler" }, { key: "herse", label: "Herse" },
  { key: "enseigne_magasin", label: "Enseigne magasin" }, { key: "faux_plafond", label: "Faux plafond" },
  { key: "autre", label: "Autre" },
];

const REQUIRED_PHOTOS = [
  { key: "vue_globale", label: "Vue générale de face" },
  { key: "allee_clients", label: "Vue depuis l'allée clients" },
  { key: "cote_gauche", label: "Côté gauche" },
  { key: "cote_droit", label: "Côté droit" },
  { key: "plafond", label: "Plafond complet au-dessus du corner" },
  { key: "electricite", label: "Zone du futur tableau électrique" },
  { key: "eau", label: "Arrivée d'eau" },
  { key: "evacuation", label: "Évacuation" },
  { key: "acces_livraison", label: "Quai / point de déchargement" },
  { key: "parcours_livraison", label: "Parcours livraison" },
  { key: "obstacles_sol", label: "Tout obstacle ou particularité" },
  { key: "environnement", label: "Vue générale de l'environnement" },
];

const ScoreBadge = ({ score }) => {
  const c = {
    conforme: { label: "Conforme", color: "bg-green-500 text-white", icon: CheckCircle2 },
    vigilance: { label: "Vigilance", color: "bg-amber-500 text-white", icon: AlertTriangle },
    bloquant: { label: "Bloquant", color: "bg-red-600 text-white", icon: ShieldAlert },
  }[score] || { label: "Conforme", color: "bg-green-500 text-white", icon: CheckCircle2 };
  const Icon = c.icon;
  return <Badge className={`${c.color} text-sm px-3 py-1`}><Icon className="w-4 h-4 mr-1.5" />{c.label}</Badge>;
};

const Bloc = ({ id, title, icon: Icon, children, defaultOpen = true, alerts = [], progress }) => {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (!id) return undefined;
    const h = (e) => { if (e.detail === id) setOpen(true); };
    window.addEventListener('prereq-open-bloc', h);
    return () => window.removeEventListener('prereq-open-bloc', h);
  }, [id]);
  const hasBloquant = alerts.some(a => a.level === 'bloquant');
  const hasAlerte = alerts.some(a => a.level === 'alerte');
  return (
    <Card id={id ? `bloc-${id}` : undefined} data-testid={id ? `bloc-${id}` : undefined} className={`scroll-mt-4 ${hasBloquant ? 'border-red-300 bg-red-50/30' : hasAlerte ? 'border-amber-300 bg-amber-50/30' : ''}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors rounded-t-lg" type="button">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-amber-600" />
          <span className="font-semibold text-slate-800">{title}</span>
          {hasBloquant && <Badge className="bg-red-100 text-red-700 text-xs ml-2">Bloquant</Badge>}
          {!hasBloquant && hasAlerte && <Badge className="bg-amber-100 text-amber-700 text-xs ml-2">Alerte</Badge>}
        </div>
        {progress && <BlocGauge done={progress[0]} total={progress[1]} testid={id ? `gauge-${id}` : undefined} />}
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && <CardContent className="pt-0 pb-5 px-5 space-y-4">{children}</CardContent>}
    </Card>
  );
};

const YesNo = ({ value, onChange, testId }) => (
  <Select value={value || ''} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
    <SelectContent>
      <SelectItem value="oui">Oui</SelectItem>
      <SelectItem value="non">Non</SelectItem>
    </SelectContent>
  </Select>
);

const BlocGauge = ({ done, total, testid }) => {
  const pct = total ? Math.round(done / total * 100) : 0;
  const color = pct >= 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <span className="ml-auto mr-3 flex items-center gap-2" data-testid={testid} title={`${done} / ${total} renseigné(s)`}>
      <span className="w-20 h-1.5 rounded-full bg-slate-200 overflow-hidden"><span className={`block h-full ${color} transition-all`} style={{ width: `${pct}%` }} /></span>
      <span className={`text-xs tabular-nums ${pct >= 100 ? 'text-emerald-600 font-semibold' : 'text-slate-500'}`}>{done}/{total}</span>
    </span>
  );
};

// Champs comptés dans la jauge de complétion de chaque bloc (indicatif, ne bloque pas l'envoi)
const filled = (v) => Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && String(v).trim() !== '';
const BLOC_FIELDS = {
  identification: ['magasin', 'enseigne', 'adresse', 'code_postal', 'ville', 'contact_magasin_nom', 'contact_magasin_telephone', 'date_visite_terrain', 'wx.type_projet'],
  equipements: ['wx.equipements'],
  emplacement: ['zone_implantation', 'emplacement_valide'],
  dimensions: ['longueur_cm', 'largeur_cm', 'obstacle_sol', 'wx.poteau_coffrage', 'wx.sol_plan'],
  hauteur_plafond: ['hauteur_sous_plafond_cm', 'hauteur_libre_reelle_cm', 'wx.hauteur_libre_max_cm'],
  electricite: ['electricite_presence', 'electricite_type', 'electricite_distance_m', 'tableau_electrique_accessible', 'electricite_travaux'],
  eau: ['eau_froide', 'eau_chaude', 'evacuation', 'eau_distance_m', 'eau_faisabilite'],
  logistique: ['acces_livraison', 'logistique_distance_quai_m', 'semi_remorque', 'chariot_elevateur', 'zone_stockage', 'contraintes_manutention'],
};
const blocProgress = (form, id) => {
  const wx = form.word_extra || {};
  const list = BLOC_FIELDS[id] || [];
  return [list.filter(f => filled(f.startsWith('wx.') ? wx[f.slice(3)] : form[f])).length, list.length];
};

const SubHead = ({ children }) => <p className="text-xs font-medium text-slate-500 uppercase tracking-wider border-t pt-4">{children}</p>;

// Questions Oui/Non qui alimentent aussi une case de la fiche Word (évite la double saisie)
const CHECK_LINKS = {
  tableau_electrique_accessible: ['elec_checks', 'accessible'],
  eau_froide: ['plomberie_checks', 'arrivee_eau'],
  evacuation: ['plomberie_checks', 'evac_dispo'],
  semi_remorque: ['livraison_checks', 'pl'],
  zone_stockage: ['livraison_checks', 'stockage'],
};
const NUM_LINKS = { hauteur_libre_reelle_cm: 'hauteur_libre_min_cm', logistique_distance_quai_m: 'distance_quai_corner_m' };
const BLOC_KEYWORDS = [[/photo/i, 'photos'], [/herse/i, 'intermarche'], [/électri|electri|tableau/i, 'electricite'], [/eau|évacuation|evacuation|plomb/i, 'eau'],
  [/livraison|quai|logisti|manutention|stockage/i, 'logistique'], [/hauteur|plafond/i, 'hauteur_plafond'], [/dimension|largeur|longueur|profondeur|obstacle au sol/i, 'dimensions'],
  [/emplacement|zone|valid/i, 'emplacement'], [/magasin|enseigne|ville|adresse/i, 'identification']];
const blocFor = (txt) => (BLOC_KEYWORDS.find(([re]) => re.test(txt)) || [])[1];
const openBloc = (id) => {
  if (!id) return;
  window.dispatchEvent(new CustomEvent('prereq-open-bloc', { detail: id }));
  setTimeout(() => document.getElementById(`bloc-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
};

const PointsATraiter = ({ alerts = [], missing = [] }) => {
  const photoMissing = missing.find(m => /photo/i.test(m));
  const shownAlerts = alerts.filter(a => !(photoMissing && a.bloc === 'photos'));
  const items = [
    ...shownAlerts.map(a => ({ level: a.level === 'bloquant' ? 'bloquant' : 'alerte', text: a.message,
      bloc: a.bloc === 'implantation' ? (/hauteur/i.test(a.message) ? 'hauteur_plafond' : 'dimensions') : a.bloc })),
    ...missing.map(m => ({ level: 'manquant', text: m, bloc: blocFor(m) })),
  ];
  if (!items.length) return null;
  const nb = shownAlerts.filter(a => a.level === 'bloquant').length;
  const style = { bloquant: 'text-red-800 bg-red-50 border-red-200', alerte: 'text-amber-800 bg-amber-50 border-amber-200', manquant: 'text-slate-700 bg-white border-slate-200' };
  const Icon = { bloquant: ShieldAlert, alerte: AlertTriangle, manquant: Clock };
  return (
    <Card className={nb ? 'border-red-300' : 'border-amber-300'} data-testid="points-a-traiter">
      <CardContent className="p-4 space-y-2">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" /> Points à traiter — {items.length}
          {nb > 0 && <Badge className="bg-red-100 text-red-700 text-xs">{nb} bloquant(s)</Badge>}
        </p>
        {items.map((it, i) => {
          const I = Icon[it.level];
          return (
            <button key={i} type="button" onClick={() => openBloc(it.bloc)} data-testid={`point-${i}`}
              className={`w-full flex items-start gap-2 text-left px-3 py-2 rounded-md border text-xs transition-colors hover:brightness-95 ${style[it.level]}`}>
              <I className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="flex-1">{it.text}</span>
              {it.bloc && <span className="shrink-0 underline opacity-70">Compléter</span>}
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

// Liste de cases à cocher (sections Fiche Word)
const WordCheckList = ({ items, selected = [], onToggle, cols = 1 }) => (
  <div className={`grid ${cols === 2 ? 'sm:grid-cols-2' : 'grid-cols-1'} gap-1.5`}>
    {items.map(([key, label]) => {
      const on = (selected || []).includes(key);
      return (
        <button key={key} type="button" onClick={() => onToggle(key)}
          className={`flex items-center gap-2 text-left text-sm px-2.5 py-1.5 rounded-md border transition-colors ${on ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          data-testid={`wcheck-${key}`}>
          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300'}`}>{on ? '✓' : ''}</span>
          {label}
        </button>
      );
    })}
  </div>
);

// Pad de signature dessinée (canvas) — stocke un dataURL PNG
const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; const c = canvasRef.current.getContext('2d'); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = canvasRef.current.getContext('2d'); const p = pos(e); c.lineWidth = 2; c.lineCap = 'round'; c.strokeStyle = '#0f172a'; c.lineTo(p.x, p.y); c.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(canvasRef.current.toDataURL('image/png')); };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); onChange(''); };
  return (
    <div className="space-y-1">
      {value ? (
        <img src={value} alt="signature" className="h-20 border rounded bg-white object-contain" />
      ) : (
        <canvas ref={canvasRef} width={260} height={80}
          className="border border-dashed border-slate-300 rounded bg-white touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      )}
      <button type="button" onClick={clear} className="text-xs text-slate-400 hover:text-red-500">Effacer</button>
    </div>
  );
};

const PhotoUpload = ({ category, label, photo, prereqId, token, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const photoUrl = photo ? `${API}/api/prerequis-techniques/${prereqId}/photos/${category}` : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/prerequis-techniques/${prereqId}/photos/${category}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (res.ok) { toast.success(`Photo "${label}" uploadée`); onUploaded(); }
      else toast.error('Erreur upload');
    } catch { toast.error('Erreur réseau'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API}/api/prerequis-techniques/${prereqId}/photos/${category}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Photo supprimée');
      onUploaded();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 bg-white" data-testid={`photo-${category}`}>
      {photo ? (
        <>
          <img src={photoUrl} alt={label} className="w-14 h-14 rounded object-cover border" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{label}</p>
            <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Uploadée</p>
          </div>
          <button onClick={handleDelete} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
        </>
      ) : (
        <>
          <div className="w-14 h-14 rounded border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin text-amber-500" /> : <ImageIcon className="w-5 h-5 text-slate-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">{label}</p>
            <p className="text-xs text-red-500">Obligatoire</p>
          </div>
          <label className="cursor-pointer">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200 hover:bg-amber-100 transition-colors">
              <Upload className="w-3 h-3" /> Upload
            </span>
          </label>
        </>
      )}
    </div>
  );
};

const WORKFLOW_STEPS = [
  { key: 'creation', label: 'Création', statuses: ['brouillon'] },
  { key: 'saisie', label: 'Saisie terrain', statuses: ['incomplet', 'a_completer', 'a_corriger'] },
  { key: 'soumis', label: 'Soumis', statuses: ['soumis'] },
  { key: 'decision', label: 'Décision', statuses: ['valide', 'valide_reserves', 'refuse'] },
];

const ProgressionBar = ({ status }) => {
  const currentIdx = WORKFLOW_STEPS.findIndex(s => s.statuses.includes(status));
  return (
    <div className="flex items-center gap-1 p-3 bg-white border rounded-xl" data-testid="progression-bar">
      {WORKFLOW_STEPS.map((step, idx) => {
        const isActive = step.statuses.includes(status);
        const isPast = idx < currentIdx;
        return (
          <React.Fragment key={step.key}>
            {idx > 0 && <div className={`flex-1 h-0.5 ${isPast || isActive ? 'bg-amber-400' : 'bg-slate-200'}`} />}
            <div className="flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                isActive ? 'bg-amber-500 text-white border-amber-500' :
                isPast ? 'bg-amber-100 text-amber-700 border-amber-400' :
                'bg-white text-slate-400 border-slate-200'
              }`}>{idx + 1}</div>
              <span className={`text-xs whitespace-nowrap ${isActive ? 'font-semibold text-amber-700' : isPast ? 'text-amber-600' : 'text-slate-400'}`}>{step.label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};


export default function PrerequisTechniquesFormPage() {
  const { simulationId, prereqId } = useParams();
  const navigate = useNavigate();
  const { token, user, isAdmin } = useAuth();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [statusChanging, setStatusChanging] = useState(false);
  const [showEnvoi, setShowEnvoi] = useState(false);
  const [eqNotes, setEqNotes] = useState([]);
  const [validationComment, setValidationComment] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSync, setPendingSync] = useState(false);
  const saveTimerRef = useRef(null);

  // Offline detection
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline = () => {
      setIsOffline(false);
      // Sync pending offline changes
      const offlineKey = `prereq_offline_${prereqId || simulationId}`;
      const offlineData = localStorage.getItem(offlineKey);
      if (offlineData) {
        setPendingSync(true);
        const parsed = JSON.parse(offlineData);
        const docId = parsed._docId;
        if (docId) {
          fetch(`${API}/api/prerequis-techniques/${docId}`, {
            method: 'PUT', headers, body: JSON.stringify(parsed),
          }).then(res => {
            if (res.ok) {
              localStorage.removeItem(offlineKey);
              setPendingSync(false);
              toast.success('Modifications hors-ligne synchronisées');
            }
          }).catch(() => setPendingSync(false));
        }
      }
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => { window.removeEventListener('offline', goOffline); window.removeEventListener('online', goOnline); };
  }, [prereqId, simulationId, token]);

  // Backfill empty address fields from simulation CAPEX data
  const backfillFromSimulation = useCallback(async (prereqDoc) => {
    const simId = prereqDoc.simulation_id;
    if (!simId) return;
    const adresseInvalid = !prereqDoc.adresse || prereqDoc.adresse.length < 5;
    const cpInvalid = !prereqDoc.code_postal;
    const zoneInvalid = !prereqDoc.zone_implantation;
    const noteInvalid = !prereqDoc.note_emplacement;
    const projetInvalid = !prereqDoc.projet_corner;
    if (!adresseInvalid && !cpInvalid && !zoneInvalid && !noteInvalid && !projetInvalid) return;
    try {
      const simRes = await fetch(`${API}/api/simulations/${simId}`, { headers });
      if (!simRes.ok) return;
      const sim = await simRes.json();
      const gi = sim.input_data?.general_info || {};
      const capex = sim.input_data?.capex_investment || {};
      const updates = {};
      if (adresseInvalid && gi.address) updates.adresse = gi.address;
      if (cpInvalid && gi.postal_code) updates.code_postal = gi.postal_code;
      if (!prereqDoc.ville && gi.city) updates.ville = gi.city;
      if (zoneInvalid && capex.corner_location) updates.zone_implantation = capex.corner_location;
      if (noteInvalid && capex.location_rating) updates.note_emplacement = capex.location_rating;
      if (projetInvalid && capex.corner_project) updates.projet_corner = capex.corner_project;
      if (Object.keys(updates).length > 0) {
        setForm(prev => ({ ...prev, ...updates }));
        await fetch(`${API}/api/prerequis-techniques/${prereqDoc.id}`, {
          method: 'PUT', headers, body: JSON.stringify(updates),
        });
      }
    } catch {}
  }, [token]);

  const applyEquipements = useCallback(async (force, id) => {
    try {
      const res = await fetch(`${API}/api/prerequis-techniques/${id || data?.id}/equipements-suggeres${force ? '?force=true' : ''}`, { method: 'POST', headers });
      if (!res.ok) return;
      const d = await res.json();
      setEqNotes(d.notes || []);
      if (d.applied) setForm(prev => ({ ...prev, word_extra: { ...(prev.word_extra || {}), equipements: d.equipements, equipements_source: d.sources } }));
      if (force) toast[d.keys.length ? 'success' : 'info'](d.keys.length ? 'Équipements repris depuis la simulation' : 'Aucun équipement identifiable dans la simulation');
    } catch {}
  }, [token, data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = useCallback(async (id) => {
    const res = await fetch(`${API}/api/prerequis-techniques/${id}`, { headers });
    if (res.ok) {
      const d = await res.json();
      setData(d); setForm(d);
      await backfillFromSimulation(d);
      if (d.simulation_id) applyEquipements(false, d.id);
    }
  }, [token, backfillFromSimulation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check access
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/prerequis-techniques-access`, { headers });
        if (res.ok) {
          const d = await res.json();
          if (d.is_blocked) setAccessBlocked(true);
        }
      } catch {}
    })();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        if (prereqId) {
          await loadData(prereqId);
        } else if (simulationId) {
          const checkRes = await fetch(`${API}/api/prerequis-techniques/by-simulation/${simulationId}`, { headers });
          if (checkRes.ok) {
            const check = await checkRes.json();
            if (check.exists) {
              setData(check); setForm(check);
              await backfillFromSimulation(check);
              if (check.simulation_id) applyEquipements(false, check.id);
            }
            else {
              const createRes = await fetch(`${API}/api/prerequis-techniques`, {
                method: 'POST', headers, body: JSON.stringify({ simulation_id: simulationId }),
              });
              if (createRes.ok) {
                const created = await createRes.json();
                const loadId = created.already_exists ? created.id : created.id;
                await loadData(loadId);
              }
            }
          }
        }
      } catch { toast.error('Erreur réseau'); }
      finally { setLoading(false); }
    })();
  }, [token, simulationId, prereqId]);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Save to localStorage for offline resilience
    const offlineKey = `prereq_offline_${prereqId || simulationId}`;
    try {
      const current = JSON.parse(localStorage.getItem(offlineKey) || '{}');
      current[field] = value;
      if (data?.id) current._docId = data.id;
      localStorage.setItem(offlineKey, JSON.stringify(current));
    } catch {}
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (!isOffline) {
      saveTimerRef.current = setTimeout(() => saveAll(), 4000);
    }
  };

  const buildPayload = () => {
    const fields = [
      'magasin','ville','adresse','code_postal','enseigne','enseigne_autre',
      'contact_magasin_nom','contact_magasin_fonction','contact_magasin_telephone','date_visite_terrain',
      'zone_implantation','zone_implantation_autre','emplacement_valide','valideur_nom','valideur_fonction','valideur_date',
      'obstacle_sol','obstacle_sol_commentaire','dimensions_commentaire','obstacles_plafond','obstacle_plafond_autre',
      'herse_decorative','position_herse',
      'electricite_presence','electricite_type','electricite_puissance_connue','electricite_puissance_valeur',
      'tableau_electrique_accessible','electricite_travaux',
      'eau_froide','eau_chaude','evacuation','eau_faisabilite',
      'acces_livraison','semi_remorque','chariot_elevateur','chariot_jour_installation',
      'zone_stockage','zone_stockage_localisation','zone_stockage_capacite','chemin_acces_degage',
      'contraintes_manutention','contraintes_manutention_commentaire',
      'observations_terrain','risques_identifies','demandes_specifiques','points_a_confirmer',
    ];
    const payload = {};
    fields.forEach(f => { if (form[f] !== undefined && form[f] !== null) payload[f] = form[f]; });
    ['longueur_cm','largeur_cm','hauteur_sous_plafond_cm','hauteur_libre_reelle_cm',
     'hauteur_sous_herse_cm','largeur_herse_cm','electricite_distance_m','eau_distance_m','logistique_distance_quai_m'
    ].forEach(f => { payload[f] = form[f] ? parseFloat(form[f]) : null; });
    if (form.word_extra && typeof form.word_extra === 'object') payload.word_extra = form.word_extra;
    return payload;
  };

  const updateWord = (key, value) => {
    setForm(prev => {
      const wx = { ...(prev.word_extra || {}), [key]: value };
      const next = { ...prev, word_extra: wx };
      const offlineKey = `prereq_offline_${prereqId || simulationId}`;
      try {
        const current = JSON.parse(localStorage.getItem(offlineKey) || '{}');
        current.word_extra = wx;
        if (data?.id) current._docId = data.id;
        localStorage.setItem(offlineKey, JSON.stringify(current));
      } catch {}
      return next;
    });
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (!isOffline) saveTimerRef.current = setTimeout(() => saveAll(), 4000);
  };

  const wx = form.word_extra || {};
  const [pdfLoading, setPdfLoading] = useState(false);
  const downloadPdf = async () => {
    if (!data.id) return;
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/api/prerequis-techniques/${data.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fiche_prerequis_${data.nom_magasin || data.ville || data.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Erreur lors de la génération du PDF'); }
    finally { setPdfLoading(false); }
  };
  const toggleWordCheck = (key, item) => {
    const arr = Array.isArray(wx[key]) ? wx[key] : [];
    updateWord(key, arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);
  };
  const updateLinked = (field, value) => {
    updateField(field, value);
    if (CHECK_LINKS[field]) {
      const [k, item] = CHECK_LINKS[field];
      const arr = (Array.isArray(wx[k]) ? wx[k] : []).filter(x => x !== item);
      updateWord(k, value === 'oui' ? [...arr, item] : arr);
    }
    if (NUM_LINKS[field]) updateWord(NUM_LINKS[field], value ? parseFloat(value) : null);
  };
  const copyContactTo = () => {
    updateField('valideur_nom', form.contact_magasin_nom || '');
    updateField('valideur_fonction', form.contact_magasin_fonction || '');
  };

  const saveAll = async () => {
    if (!data?.id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/prerequis-techniques/${data.id}`, {
        method: 'PUT', headers, body: JSON.stringify(buildPayload()),
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated);
        setForm(prev => ({ ...prev, alerts: updated.alerts, score: updated.score, updated_at: updated.updated_at }));
      }
    } catch {}
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/prerequis-techniques/${data.id}`, {
        method: 'PUT', headers, body: JSON.stringify(buildPayload()),
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated); setForm(prev => ({ ...prev, ...updated }));
        toast.success('Sauvegardé');
      } else toast.error('Erreur de sauvegarde');
    } catch { toast.error('Erreur réseau'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (newStatus) => {
    if (!data?.id) return;
    // Save first before submitting
    if (newStatus === 'soumis') await handleSave();
    setStatusChanging(true);
    try {
      const res = await fetch(`${API}/api/prerequis-techniques/${data.id}/status`, {
        method: 'PUT', headers,
        body: JSON.stringify({ status: newStatus, commentaire: validationComment || null }),
      });
      if (res.ok) {
        const updated = await res.json();
        setData(updated); setForm(prev => ({ ...prev, ...updated }));
        setValidationComment('');
        toast.success(`Statut mis à jour: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setStatusChanging(false); }
  };


  const surfaceM2 = form.longueur_cm && form.largeur_cm
    ? ((parseFloat(form.longueur_cm) * parseFloat(form.largeur_cm)) / 10000).toFixed(2) : null;

  const alertsFor = (bloc) => (data?.alerts || []).filter(a => a.bloc === bloc);
  const photosChecklist = data?.photos_checklist || {};
  const photosUploaded = Object.keys(photosChecklist).filter(k => REQUIRED_PHOTOS.some(p => p.key === k)).length;
  const photosTotal = REQUIRED_PHOTOS.length;
  const prog = (id) => blocProgress(form, id);
  const allProg = [...Object.keys(BLOC_FIELDS).map(prog), [photosUploaded, photosTotal]];
  const completion = Math.round(allProg.reduce((t, p) => t + p[0], 0) / allProg.reduce((t, p) => t + p[1], 0) * 100);
  const pointsCount = (data?.alerts || []).length + (data?.missing_items || []).length;

  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div></AppLayout>;
  if (accessBlocked) return <AppLayout><div className="text-center py-16"><ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-3" /><p className="text-lg font-semibold text-slate-700">Accès restreint</p><p className="text-sm text-slate-500 mt-1">Vous n'avez pas accès au module Prérequis Techniques.</p><Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Retour</Button></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-center py-16 text-slate-500">Prérequis non trouvé</div></AppLayout>;

  const sc = STATUS_CONFIG[data.status] || STATUS_CONFIG.brouillon;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-5 pb-12" data-testid="prerequis-form">
        {/* Offline banner */}
        {isOffline && (
          <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="offline-banner">
            <WifiOff className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">Mode hors-ligne</p>
              <p className="text-xs text-amber-600">Vos modifications sont sauvegardées localement. Elles seront synchronisées automatiquement à la reconnexion.</p>
            </div>
          </div>
        )}
        {pendingSync && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-300 rounded-lg">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
            <p className="text-sm text-blue-800">Synchronisation des modifications hors-ligne en cours...</p>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Prérequis Techniques</h1>
              <p className="text-sm text-slate-500" data-testid="prereq-subtitle">{[form.magasin,
                form.enseigne && !(form.magasin || '').toLowerCase().includes(form.enseigne.toLowerCase()) ? form.enseigne : null,
                form.ville && !(form.magasin || '').toLowerCase().includes(form.ville.toLowerCase()) ? form.ville : null,
              ].filter(Boolean).join(' — ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ScoreBadge score={data.score} />
            <Badge className={`${sc.color} text-xs`}>{sc.label}</Badge>
          </div>
        </div>

        <div className="flex items-center gap-3 px-4 py-3 bg-white border rounded-xl" data-testid="prereq-completion">
          <span className="text-sm font-semibold text-slate-700">Dossier complété à {completion} %</span>
          <span className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden"><span className={`block h-full ${completion >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${completion}%` }} /></span>
          <span className="text-xs text-slate-500">Indicatif — l'envoi reste possible</span>
        </div>

        {/* Points à traiter (alertes + éléments manquants, une seule fois) */}
        <PointsATraiter alerts={data.alerts || []}
          missing={['brouillon', 'incomplet', 'a_completer', 'a_corriger'].includes(data.status) ? (data.missing_items || []) : []} />

        {/* Bloc 1 — Identification */}
        <Bloc id="identification" progress={prog('identification')} title="Identification du site" icon={Building2}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Magasin *</Label><Input value={form.magasin || ''} onChange={e => updateField('magasin', e.target.value)} data-testid="field-magasin" /></div>
            <div className="space-y-1.5"><Label>Enseigne *</Label>
              <Select value={form.enseigne || ''} onValueChange={v => updateField('enseigne', v)}>
                <SelectTrigger data-testid="field-enseigne"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{ENSEIGNE_OPTIONS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.enseigne === 'Autre' && <div className="space-y-1.5 col-span-2"><Label>Précisez *</Label><Input value={form.enseigne_autre || ''} onChange={e => updateField('enseigne_autre', e.target.value)} /></div>}
            <div className="space-y-1.5 col-span-2"><Label>Adresse</Label><Input value={form.adresse || ''} onChange={e => updateField('adresse', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Code postal</Label><Input value={form.code_postal || ''} onChange={e => updateField('code_postal', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Ville</Label><Input value={form.ville || ''} onChange={e => updateField('ville', e.target.value)} /></div>
          </div>
          <div className="border-t pt-4 mt-2">
            <p className="text-xs font-medium text-slate-500 mb-3 uppercase tracking-wider">Contact magasin</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Nom du contact</Label><Input value={form.contact_magasin_nom || ''} onChange={e => updateField('contact_magasin_nom', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Fonction</Label><Input value={form.contact_magasin_fonction || ''} onChange={e => updateField('contact_magasin_fonction', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Téléphone</Label><Input value={form.contact_magasin_telephone || ''} onChange={e => updateField('contact_magasin_telephone', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Date de visite terrain</Label><Input type="date" value={form.date_visite_terrain || ''} onChange={e => updateField('date_visite_terrain', e.target.value)} /></div>
            </div>
          </div>
          <SubHead>Type de projet</SubHead>
          <WordCheckList cols={3} selected={wx.type_projet ? [wx.type_projet] : []}
            onToggle={(k) => updateWord('type_projet', wx.type_projet === k ? '' : k)}
            items={[['nouveau', 'Nouveau'], ['remodeling', 'Remodeling'], ['deplacement', 'Déplacement']]} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Commercial</Label><Input value={wx.commercial_nom || ''} onChange={e => updateWord('commercial_nom', e.target.value)} data-testid="field-commercial" /></div>
          </div>
        </Bloc>

        <Bloc id="equipements" progress={prog('equipements')} title="Équipements prévus" icon={ImageIcon}>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {(wx.equipements_source || []).length > 0 && <span data-testid="equipements-source">Pré-coché depuis : {wx.equipements_source.join(', ')}</span>}
            {data.simulation_id && <button type="button" onClick={() => applyEquipements(true)} className="underline text-amber-700" data-testid="equipements-reapply">Reprendre depuis la simulation</button>}
          </div>
          {eqNotes.map((n, i) => <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">{n}</p>)}
          <WordCheckList cols={2} selected={wx.equipements} onToggle={(k) => toggleWordCheck('equipements', k)}
            items={[['vitrine', 'Vitrine réfrigérée'], ['saladette', 'Saladette'], ['cellule3', 'Cellule 3 niveaux'], ['cellule5', 'Cellule 5 niveaux'], ['wok', 'Wok induction'], ['plaque', 'Plaque induction'], ['cuiseur_riz', 'Cuiseur(s) à riz'], ['chauffe_eau', 'Chauffe-eau'], ['plonge', 'Plonge'], ['ecran', 'Écran'], ['enseigne_haute', 'Enseigne haute']]} />
        </Bloc>

        {/* Bloc 2 — Emplacement */}
        <Bloc id="emplacement" progress={prog('emplacement')} title="Emplacement" icon={MapPin} alerts={alertsFor('emplacement')}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Zone d'implantation *</Label>
              <Select value={form.zone_implantation || ''} onValueChange={v => updateField('zone_implantation', v)}>
                <SelectTrigger data-testid="field-zone"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>{ZONE_OPTIONS.map(z => <SelectItem key={z} value={z}>{z}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Note de l'emplacement</Label>
              <div className="flex items-center h-9 px-3 bg-slate-100 rounded-md border text-sm" data-testid="field-note-emplacement">{form.note_emplacement || '—'}</div>
            </div>
            <div className="space-y-1.5"><Label>Projet du corner</Label>
              <div className="flex items-center h-9 px-3 bg-slate-100 rounded-md border text-sm" data-testid="field-projet-corner">{form.projet_corner || '—'}</div>
            </div>
          </div>
          {form.zone_implantation === 'Autre' && <div className="space-y-1.5"><Label>Précisez *</Label><Input value={form.zone_implantation_autre || ''} onChange={e => updateField('zone_implantation_autre', e.target.value)} /></div>}
          <div className="space-y-1.5"><Label>Emplacement validé par le magasin *</Label><YesNo value={form.emplacement_valide} onChange={v => updateField('emplacement_valide', v)} testId="field-emplacement-valide" /></div>
          {form.emplacement_valide === 'oui' && form.contact_magasin_nom && (
            <button type="button" onClick={() => copyContactTo('valideur')} data-testid="copy-contact-valideur"
              className="text-xs font-medium text-amber-700 underline">Même personne que le contact magasin ({form.contact_magasin_nom})</button>
          )}
          {form.emplacement_valide === 'oui' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="space-y-1.5"><Label>Nom du valideur</Label><Input value={form.valideur_nom || ''} onChange={e => updateField('valideur_nom', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Fonction</Label><Input value={form.valideur_fonction || ''} onChange={e => updateField('valideur_fonction', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.valideur_date || ''} onChange={e => updateField('valideur_date', e.target.value)} /></div>
            </div>
          )}
          {form.emplacement_valide === 'non' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"><ShieldAlert className="w-5 h-5" /> Dossier non finalisable — emplacement non validé</div>
          )}
        </Bloc>

        {/* Bloc 3 — Dimensions */}
        <Bloc id="dimensions" progress={prog('dimensions')} title="Dimensions au sol" icon={Ruler} alerts={[...alertsFor('dimensions'), ...alertsFor('implantation').filter(a => !/hauteur/i.test(a.message))]}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Profondeur (cm) *</Label><Input type="number" value={form.longueur_cm || ''} onChange={e => updateField('longueur_cm', e.target.value)} placeholder="Ex: 350" /></div>
            <div className="space-y-1.5"><Label>Largeur (cm) *</Label><Input type="number" value={form.largeur_cm || ''} onChange={e => updateField('largeur_cm', e.target.value)} placeholder="Ex: 250" /></div>
            <div className="space-y-1.5"><Label>Surface (m²)</Label><div className="flex items-center h-9 px-3 bg-slate-100 rounded-md border text-sm font-semibold">{surfaceM2 ? `${surfaceM2} m²` : '—'}</div></div>
          </div>
          <div className="space-y-1.5"><Label>Obstacle au sol</Label><YesNo value={form.obstacle_sol} onChange={v => updateField('obstacle_sol', v)} testId="field-obstacle-sol" /></div>
          {form.obstacle_sol === 'oui' && <div className="space-y-1.5"><Label>Commentaire *</Label><Textarea value={form.obstacle_sol_commentaire || ''} onChange={e => updateField('obstacle_sol_commentaire', e.target.value)} rows={2} /></div>}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Poteau / coffrage dans l'emprise</Label><YesNo value={wx.poteau_coffrage} onChange={v => updateWord('poteau_coffrage', v)} testId="field-poteau" /></div>
            <div className="space-y-1.5"><Label>Sol plan</Label><YesNo value={wx.sol_plan} onChange={v => updateWord('sol_plan', v)} testId="field-sol-plan" /></div>
            <div className="space-y-1.5"><Label>Mur/support arrière exploitable</Label><YesNo value={wx.mur_support_arriere} onChange={v => updateWord('mur_support_arriere', v)} testId="field-mur-arriere" /></div>
            <div className="space-y-1.5"><Label>Implantation conforme au plan</Label><YesNo value={wx.implantation_conforme} onChange={v => updateWord('implantation_conforme', v)} testId="field-implantation-conforme" /></div>
          </div>
          <div className="space-y-1.5"><Label>Commentaires</Label><Textarea value={form.dimensions_commentaire || ''} onChange={e => updateField('dimensions_commentaire', e.target.value)} rows={2} placeholder="Commentaires libres sur les dimensions..." data-testid="field-dimensions-commentaire" /></div>
        </Bloc>

        {/* Bloc 4 — Hauteur et plafond */}
        <Bloc id="hauteur_plafond" progress={prog('hauteur_plafond')} title="Hauteur, plafond & enseigne" icon={ArrowUpDown} alerts={[...alertsFor('hauteur_plafond'), ...alertsFor('implantation').filter(a => /hauteur/i.test(a.message))]}>
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-xs mb-2">Point critique : l'enseigne aérienne WASABI culmine à <strong>270 cm</strong>. Cette hauteur doit être totalement libre sur toute l'emprise.</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Hauteur sous plafond (cm)</Label><Input type="number" value={form.hauteur_sous_plafond_cm || ''} onChange={e => updateField('hauteur_sous_plafond_cm', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Hauteur libre maximale (cm)</Label><Input type="number" value={wx.hauteur_libre_max_cm || ''} onChange={e => updateWord('hauteur_libre_max_cm', e.target.value ? parseFloat(e.target.value) : null)} data-testid="field-hauteur-max" /></div>
            <div className="space-y-1.5"><Label>Hauteur libre minimale réelle (cm) *</Label>
              <Input type="number" value={form.hauteur_libre_reelle_cm || wx.hauteur_libre_min_cm || ''} onChange={e => updateLinked('hauteur_libre_reelle_cm', e.target.value)} data-testid="field-hauteur-libre"
                className={form.hauteur_libre_reelle_cm && parseFloat(form.hauteur_libre_reelle_cm) < 270 ? 'border-red-400 bg-red-50' : ''} />
              {form.hauteur_libre_reelle_cm && parseFloat(form.hauteur_libre_reelle_cm) < 270 && <p className="text-xs text-red-600 font-medium flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> ALERTE TECHNIQUE</p>}
            </div>
          </div>
          <div className="space-y-2"><Label>Obstacles plafond</Label>
            <div className="flex flex-wrap gap-2">
              {OBSTACLES_PLAFOND.map(ob => {
                const checked = (form.obstacles_plafond || []).includes(ob.key);
                return <button key={ob.key} type="button" onClick={() => updateField('obstacles_plafond', checked ? (form.obstacles_plafond || []).filter(k => k !== ob.key) : [...(form.obstacles_plafond || []), ob.key])}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${checked ? 'bg-amber-100 border-amber-400 text-amber-800 font-medium' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>{ob.label}</button>;
              })}
            </div>
          </div>
          {(form.obstacles_plafond || []).includes('autre') && <div className="space-y-1.5"><Label>Précisez *</Label><Input value={form.obstacle_plafond_autre || ''} onChange={e => updateField('obstacle_plafond_autre', e.target.value)} /></div>}
          <SubHead>Contrôles plafond & enseigne</SubHead>
          <WordCheckList cols={2} selected={wx.plafond_env_checks} onToggle={(k) => toggleWordCheck('plafond_env_checks', k)}
            items={[['aucun_element_descend', "Aucun élément ne descend dans le volume du corner"], ['absence_poutre', 'Absence de poutre ou retombée décorative gênante'], ['absence_gaine', 'Absence de gaine ou luminaire gênant'], ['cameras_signaletique', 'Caméras / signalétique magasin identifiées'], ['sprinklers', 'Sprinklers et équipements incendie identifiés'], ['fixation_enseigne', "Fixation de l'enseigne possible"], ['enseigne_visible', "Enseigne visible depuis l'allée clients"], ['structure_plafond', "Structure plafond compatible avec l'implantation"]]} />
          <div className="space-y-1.5"><Label>Autre particularité constatée</Label><Textarea value={wx.obstacle_particularite || ''} onChange={e => updateWord('obstacle_particularite', e.target.value)} rows={2} /></div>
        </Bloc>

        {/* Bloc 5 — Intermarché (conditionnel) */}
        {form.enseigne === 'Intermarché' && (
          <Bloc id="intermarche" title="Bloc spécifique Intermarché" icon={Building2} alerts={alertsFor('intermarche')}>
            <div className="space-y-1.5"><Label>Présence de herse décorative *</Label>
              <Select value={form.herse_decorative || ''} onValueChange={v => updateField('herse_decorative', v)}>
                <SelectTrigger data-testid="field-herse"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui</SelectItem>
                  <SelectItem value="non">Non</SelectItem>
                  <SelectItem value="prevue">Prévue</SelectItem>
                  <SelectItem value="inconnu">Inconnu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.herse_decorative === 'inconnu' && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"><ShieldAlert className="w-5 h-5" /> Dossier bloqué — statut herse inconnu</div>
            )}
            {(form.herse_decorative === 'oui' || form.herse_decorative === 'prevue') && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5"><Label>Hauteur sous herse (cm)</Label><Input type="number" value={form.hauteur_sous_herse_cm || ''} onChange={e => updateField('hauteur_sous_herse_cm', e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Largeur (cm)</Label><Input type="number" value={form.largeur_herse_cm || ''} onChange={e => updateField('largeur_herse_cm', e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Position / stand</Label><Input value={form.position_herse || ''} onChange={e => updateField('position_herse', e.target.value)} /></div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-slate-500 uppercase">Photos herse (obligatoires)</p>
                  <PhotoUpload category="herse_1" label="Photo herse 1" photo={photosChecklist.herse_1} prereqId={data.id} token={token} onUploaded={() => loadData(data.id)} />
                  <PhotoUpload category="herse_2" label="Photo herse 2" photo={photosChecklist.herse_2} prereqId={data.id} token={token} onUploaded={() => loadData(data.id)} />
                </div>
              </>
            )}
          </Bloc>
        )}

        {/* Bloc 6 — Électricité */}
        <Bloc id="electricite" progress={prog('electricite')} title="Électricité" icon={Zap} alerts={alertsFor('electricite')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Alimentation électrique *</Label><YesNo value={form.electricite_presence} onChange={v => updateField('electricite_presence', v)} testId="field-elec-presence" /></div>
            <div className="space-y-1.5"><Label>Type alimentation</Label>
              <Select value={form.electricite_type || ''} onValueChange={v => updateField('electricite_type', v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monophase">Monophasé</SelectItem>
                  <SelectItem value="triphase">Triphasé</SelectItem>
                  <SelectItem value="inconnu">Inconnu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Distance alimentation → stand (m)</Label><Input type="number" value={form.electricite_distance_m || ''} onChange={e => updateField('electricite_distance_m', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Puissance connue</Label><YesNo value={form.electricite_puissance_connue} onChange={v => updateField('electricite_puissance_connue', v)} testId="field-elec-puissance" /></div>
          </div>
          {form.electricite_puissance_connue === 'oui' && (
            <div className="space-y-1.5"><Label>Valeur de puissance</Label><Input value={form.electricite_puissance_valeur || ''} onChange={e => updateField('electricite_puissance_valeur', e.target.value)} placeholder="Ex: 32A, 20kW..." /></div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tableau électrique accessible</Label><YesNo value={form.tableau_electrique_accessible} onChange={v => updateLinked('tableau_electrique_accessible', v)} testId="field-tableau-elec" /></div>
            <div className="space-y-1.5"><Label>Travaux à prévoir</Label><YesNo value={form.electricite_travaux} onChange={v => updateField('electricite_travaux', v)} testId="field-elec-travaux" /></div>
          </div>
          {form.electricite_presence === 'non' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"><ShieldAlert className="w-5 h-5" /> Dossier bloquant — aucune alimentation électrique</div>
          )}
          <SubHead>Tableau électrique dédié au corner</SubHead>
          <p className="text-xs text-slate-500">Tableau DÉDIÉ au corner, à l'intérieur du stand, alimenté et opérationnel avant la pose.</p>
          <WordCheckList cols={2} selected={wx.elec_checks} onToggle={(k) => toggleWordCheck('elec_checks', k)}
            items={[['magasin_informe', "Magasin informé de l'obligation du tableau dédié"], ['electricien_informe', 'Électricien du magasin informé'], ['emplacement_valide', 'Emplacement du tableau validé'], ['alim_amenee', 'Alimentation amenée jusqu\'au tableau'], ['puissance_confirmee', 'Puissance disponible confirmée par l\'électricien'], ['protections', 'Protections adaptées aux besoins du corner'], ['operationnel', 'Installation opérationnelle avant montage']]} />
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Contact électricien</Label><Input value={wx.electricien_contact || ''} onChange={e => updateWord('electricien_contact', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Téléphone électricien</Label><Input value={wx.electricien_tel || ''} onChange={e => updateWord('electricien_tel', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Date prévue travaux</Label><Input type="date" value={wx.elec_date_travaux || ''} onChange={e => updateWord('elec_date_travaux', e.target.value)} /></div>
          </div>
          <PhotoUpload category="alimentation_electrique" label="Photo alimentation électrique" photo={photosChecklist.alimentation_electrique} prereqId={data.id} token={token} onUploaded={() => loadData(data.id)} />
        </Bloc>

        {/* Bloc 7 — Eau */}
        <Bloc id="eau" progress={prog('eau')} title="Eau & évacuation" icon={Droplets} alerts={alertsFor('eau')}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Arrivée eau froide</Label><YesNo value={form.eau_froide} onChange={v => updateLinked('eau_froide', v)} testId="field-eau-froide" /></div>
            <div className="space-y-1.5"><Label>Arrivée eau chaude</Label><YesNo value={form.eau_chaude} onChange={v => updateField('eau_chaude', v)} testId="field-eau-chaude" /></div>
            <div className="space-y-1.5"><Label>Évacuation *</Label><YesNo value={form.evacuation} onChange={v => updateLinked('evacuation', v)} testId="field-evacuation" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Distance raccordement → stand (m)</Label><Input type="number" value={form.eau_distance_m || ''} onChange={e => updateField('eau_distance_m', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Faisabilité sans gros travaux</Label>
              <Select value={form.eau_faisabilite || ''} onValueChange={v => updateField('eau_faisabilite', v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui</SelectItem>
                  <SelectItem value="non">Non</SelectItem>
                  <SelectItem value="a_confirmer">À confirmer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.evacuation === 'non' && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm"><AlertTriangle className="w-5 h-5" /> Alerte technique — évacuation absente</div>
          )}
          <SubHead>Plomberie</SubHead>
          <WordCheckList cols={2} selected={wx.plomberie_checks} onToggle={(k) => toggleWordCheck('plomberie_checks', k)}
            items={[['vanne', 'Vanne d\'arrêt accessible'], ['raccordement', 'Raccordement possible'], ['diametre_adapte', 'Diamètre d\'évacuation adapté'], ['gravitaire', 'Évacuation gravitaire possible'], ['plonge_conforme', 'Emplacement plonge conforme'], ['chauffe_eau', 'Emplacement chauffe-eau disponible'], ['maintenance', 'Accès maintenance possible'], ['travaux_identifies', 'Travaux plombier magasin identifiés']]} />
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Position arrivée d'eau</Label><Input value={wx.position_arrivee_eau || ''} onChange={e => updateWord('position_arrivee_eau', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Diamètre évacuation (mm)</Label><Input type="number" value={wx.diametre_evac_mm || ''} onChange={e => updateWord('diametre_evac_mm', e.target.value ? parseFloat(e.target.value) : null)} /></div>
            <div className="space-y-1.5"><Label>Distance évacuation / plonge (mm)</Label><Input type="number" value={wx.distance_evac_plonge_mm || ''} onChange={e => updateWord('distance_evac_plonge_mm', e.target.value ? parseFloat(e.target.value) : null)} /></div>
            <div className="space-y-1.5"><Label>Contact plombier</Label><Input value={wx.plombier_contact || ''} onChange={e => updateWord('plombier_contact', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Date prévue travaux</Label><Input type="date" value={wx.plomberie_date_travaux || ''} onChange={e => updateWord('plomberie_date_travaux', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Travaux plomberie à réaliser avant pose</Label><Textarea value={wx.plomberie_travaux || ''} onChange={e => updateWord('plomberie_travaux', e.target.value)} rows={2} /></div>
          <PhotoUpload category="eau_evacuation" label="Photo eau / évacuation" photo={photosChecklist.eau_evacuation} prereqId={data.id} token={token} onUploaded={() => loadData(data.id)} />
        </Bloc>

        {/* Bloc 8 — Logistique */}
        <Bloc id="logistique" progress={prog('logistique')} title="Logistique & livraison" icon={Truck} alerts={alertsFor('logistique')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Accès livraison *</Label>
              <Select value={form.acces_livraison || ''} onValueChange={v => updateField('acces_livraison', v)}>
                <SelectTrigger data-testid="field-acces-livraison"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conforme">Conforme</SelectItem>
                  <SelectItem value="difficile">Difficile</SelectItem>
                  <SelectItem value="impossible">Impossible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Distance quai → stand (m)</Label><Input type="number" value={form.logistique_distance_quai_m || wx.distance_quai_corner_m || ''} onChange={e => updateLinked('logistique_distance_quai_m', e.target.value)} data-testid="field-distance-quai" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Semi-remorque possible</Label><YesNo value={form.semi_remorque} onChange={v => updateLinked('semi_remorque', v)} testId="field-semi" /></div>
            <div className="space-y-1.5"><Label>Chariot élévateur</Label><YesNo value={form.chariot_elevateur} onChange={v => updateField('chariot_elevateur', v)} testId="field-chariot" /></div>
            <div className="space-y-1.5"><Label>Chariot dispo jour installation</Label><YesNo value={form.chariot_jour_installation} onChange={v => updateField('chariot_jour_installation', v)} testId="field-chariot-jour" /></div>
            <div className="space-y-1.5"><Label>Zone stockage temporaire</Label><YesNo value={form.zone_stockage} onChange={v => updateLinked('zone_stockage', v)} testId="field-stockage" /></div>
          </div>
          {form.zone_stockage === 'oui' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg border">
              <div className="space-y-1.5"><Label>Localisation</Label><Input value={form.zone_stockage_localisation || ''} onChange={e => updateField('zone_stockage_localisation', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Capacité estimée</Label><Input value={form.zone_stockage_capacite || ''} onChange={e => updateField('zone_stockage_capacite', e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Chemin d'accès dégagé</Label><YesNo value={form.chemin_acces_degage} onChange={v => updateField('chemin_acces_degage', v)} testId="field-chemin" /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Contraintes manutention</Label><YesNo value={form.contraintes_manutention} onChange={v => updateField('contraintes_manutention', v)} testId="field-contraintes" /></div>
          {form.contraintes_manutention === 'oui' && (
            <div className="space-y-1.5"><Label>Commentaire *</Label><Textarea value={form.contraintes_manutention_commentaire || ''} onChange={e => updateField('contraintes_manutention_commentaire', e.target.value)} rows={2} /></div>
          )}
          {form.acces_livraison === 'impossible' && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"><ShieldAlert className="w-5 h-5" /> Dossier bloquant — accès livraison impossible</div>
          )}
          <SubHead>Parcours d'acheminement</SubHead>
          <WordCheckList cols={2} selected={wx.livraison_checks} onToggle={(k) => toggleWordCheck('livraison_checks', k)}
            items={[['quai', 'Quai disponible'], ['hayon', 'Hayon nécessaire identifié'], ['horaires', 'Horaires de livraison identifiés'], ['nuit', 'Livraison de nuit / autorisation'], ['transpalette', 'Passage transpalette possible'], ['sol', 'Sol praticable sur tout le parcours'], ['portes', 'Portes suffisamment larges'], ['monte_charge', 'Monte-charge vérifié si nécessaire'], ['escalier', 'Absence d\'escalier / seuil bloquant'], ['plus_gros', 'Plus gros élément acheminable jusqu\'au corner']]} />
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Porte la plus étroite (mm)</Label><Input type="number" value={wx.porte_etroite_mm || ''} onChange={e => updateWord('porte_etroite_mm', e.target.value ? parseFloat(e.target.value) : null)} /></div>
            <div className="space-y-1.5"><Label>Hauteur minimale parcours (mm)</Label><Input type="number" value={wx.hauteur_min_parcours_mm || ''} onChange={e => updateWord('hauteur_min_parcours_mm', e.target.value ? parseFloat(e.target.value) : null)} /></div>
            <div className="space-y-1.5"><Label>Horaires autorisés</Label><Input value={wx.horaires_autorises || ''} onChange={e => updateWord('horaires_autorises', e.target.value)} /></div>
          </div>
        </Bloc>

        {/* Bloc 9 — Photos obligatoires */}
        <Bloc id="photos" progress={[photosUploaded, photosTotal]} title={`Photos obligatoires (${photosUploaded}/${photosTotal})`} icon={Camera} alerts={alertsFor('photos')}>
          <div className="space-y-2">
            {REQUIRED_PHOTOS.map(p => (
              <PhotoUpload key={p.key} category={p.key} label={p.label} photo={photosChecklist[p.key]} prereqId={data.id} token={token} onUploaded={() => loadData(data.id)} />
            ))}
          </div>
          {photosUploaded < photosTotal && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4" /> {photosTotal - photosUploaded} photo(s) manquante(s) — le dossier restera "Incomplet"
            </div>
          )}
        </Bloc>

        {(['remodeling', 'deplacement'].includes(wx.type_projet) || (wx.remodeling_checks || []).length > 0) && (
        <Bloc id="remodeling" title="Remodeling / corner existant" icon={Building2} defaultOpen={false}>
          <WordCheckList selected={wx.remodeling_checks} onToggle={(k) => toggleWordCheck('remodeling_checks', k)}
            items={[['ancien_present', 'Ancien corner présent'], ['demontage', 'Démontage nécessaire'], ['evac_mobilier', 'Évacuation ancien mobilier nécessaire'], ['frigo_conserver', 'Matériel frigorifique à conserver'], ['a_remplacer', 'Matériel à remplacer'], ['enseigne_deposer', 'Enseigne existante à déposer'], ['ecran_conserver', 'Écran existant à conserver'], ['structures_evacuer', 'Structures / habillages à évacuer']]} />
          <div className="space-y-1.5"><Label>Matériel à conserver / évacuer — responsabilités</Label><Textarea value={wx.remodeling_responsabilites || ''} onChange={e => updateWord('remodeling_responsabilites', e.target.value)} rows={2} /></div>
        </Bloc>
        )}

        {/* Historique des modifications */}
        <Bloc title="Historique des modifications" icon={History} defaultOpen={false}>
          {(data.history || []).length === 0 ? (
            <p className="text-sm text-slate-400">Aucune modification enregistrée.</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {[...(data.history || [])].reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-2 text-sm border-b border-slate-100 pb-1.5" data-testid={`history-entry-${i}`}>
                  <span className="text-slate-400 shrink-0">{h.timestamp ? new Date(h.timestamp).toLocaleString('fr-FR') : ''}</span>
                  <span className="font-medium text-slate-700">{h.user_name || '—'}</span>
                  <span className="text-slate-500">{h.details || h.action}</span>
                </div>
              ))}
            </div>
          )}
        </Bloc>

        {/* Bloc 10 — Commentaires libres */}
        <Bloc title="Commentaires libres" icon={MessageSquare} defaultOpen={false}>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Observations terrain</Label><Textarea value={form.observations_terrain || ''} onChange={e => updateField('observations_terrain', e.target.value)} rows={3} placeholder="Observations relevées sur le terrain..." /></div>
            <div className="space-y-1.5"><Label>Risques identifiés</Label><Textarea value={form.risques_identifies || ''} onChange={e => updateField('risques_identifies', e.target.value)} rows={2} placeholder="Risques potentiels..." /></div>
            <div className="space-y-1.5"><Label>Demandes spécifiques magasin</Label><Textarea value={form.demandes_specifiques || ''} onChange={e => updateField('demandes_specifiques', e.target.value)} rows={2} placeholder="Demandes particulières du magasin..." /></div>
            <div className="space-y-1.5"><Label>Points à confirmer</Label><Textarea value={form.points_a_confirmer || ''} onChange={e => updateField('points_a_confirmer', e.target.value)} rows={2} placeholder="Points en attente de confirmation..." /></div>
          </div>
        </Bloc>

        {/* Progression bar */}
        <ProgressionBar status={data.status} />

        {/* Admin Validation Panel */}
        {isAdmin && ['soumis', 'a_corriger'].includes(data.status) && (
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="p-5 space-y-4">
              <p className="text-sm font-semibold text-blue-800 flex items-center gap-2"><ShieldCheck className="w-5 h-5" /> Décision de validation technique</p>
              <div className="space-y-1.5">
                <Label>Commentaire de validation</Label>
                <Textarea value={validationComment} onChange={e => setValidationComment(e.target.value)} rows={2} placeholder="Commentaire optionnel..." />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => changeStatus('valide')} className="bg-green-600 hover:bg-green-700 text-white" disabled={statusChanging} data-testid="btn-valide">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider techniquement
                </Button>
                <Button onClick={() => changeStatus('valide_reserves')} className="bg-yellow-500 hover:bg-yellow-600 text-white" disabled={statusChanging} data-testid="btn-valide-reserves">
                  <AlertTriangle className="w-4 h-4 mr-1.5" /> Valider avec réserves
                </Button>
                <Button onClick={() => changeStatus('refuse')} variant="destructive" disabled={statusChanging} data-testid="btn-refuse">
                  <Ban className="w-4 h-4 mr-1.5" /> Refuser
                </Button>
                <Button onClick={() => changeStatus('a_corriger')} variant="outline" disabled={statusChanging} data-testid="btn-corriger">
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Retourner à corriger
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Validation result banner */}
        {data.status === 'valide' && (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800">
            <CheckCircle2 className="w-6 h-6" />
            <div>
              <p className="font-semibold">Validé techniquement</p>
              <p className="text-sm">{data.validated_by && `Par ${data.validated_by}`} {data.validated_at && `le ${new Date(data.validated_at).toLocaleDateString('fr-FR')}`}</p>
              {data.validation_commentaire && <p className="text-sm mt-1 italic">"{data.validation_commentaire}"</p>}
            </div>
          </div>
        )}
        {data.status === 'valide_reserves' && (
          <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-yellow-800">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <p className="font-semibold">Validé avec réserves</p>
              <p className="text-sm">{data.validated_by && `Par ${data.validated_by}`} {data.validated_at && `le ${new Date(data.validated_at).toLocaleDateString('fr-FR')}`}</p>
              {data.validation_commentaire && <p className="text-sm mt-1 italic">"{data.validation_commentaire}"</p>}
            </div>
          </div>
        )}
        {data.status === 'refuse' && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
            <Ban className="w-6 h-6" />
            <div>
              <p className="font-semibold">Refusé</p>
              <p className="text-sm">{data.validated_by && `Par ${data.validated_by}`} {data.validated_at && `le ${new Date(data.validated_at).toLocaleDateString('fr-FR')}`}</p>
              {data.validation_commentaire && <p className="text-sm mt-1 italic">"{data.validation_commentaire}"</p>}
            </div>
          </div>
        )}

        <EnvoiPrerequisDialog open={showEnvoi} onClose={() => setShowEnvoi(false)} prereqId={data.id} token={token} isAdmin={isAdmin} isSuperAdmin={user?.role === 'super_admin'}
          pointsCount={pointsCount} completion={completion} beforeSend={handleSave}
          onSent={(d) => { setShowEnvoi(false); setData(d); setForm(prev => ({ ...prev, status: d.status })); }} />

        {/* Save & Submit bar */}
        <div className="flex items-center justify-between p-4 bg-white border rounded-xl shadow-sm sticky bottom-4" data-testid="form-actions">
          <div className="text-xs text-slate-400">
            {saving ? 'Sauvegarde en cours...' : data.updated_at ? `Dernière sauvegarde: ${new Date(data.updated_at).toLocaleString('fr-FR')}` : ''}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
            {data.id && (
              <Button variant="outline" onClick={downloadPdf} disabled={pdfLoading} data-testid="prereq-pdf-btn">
                {pdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                PDF
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Sauvegarder
            </Button>
            <Button onClick={() => setShowEnvoi(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-btn"
              title="Envoyer la fiche (même incomplète) à la direction et/ou à un agenceur">
              <Send className="w-4 h-4 mr-2" /> Envoyer
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
